/**
 * Effect metadata — single source of truth for all per-effect colour values
 * and display names.
 *
 * These values are injected as CSS custom properties on :root by App.vue at
 * startup (see applyEffectCssVars), so the same data drives both the JS layer
 * (pedal chart colours, etc.) and the CSS layer (pedal shell gradients, knob
 * accents, etc.) without duplication.
 *
 * Light-mode plate colours (--*-color-light, --*-color-light-dark) live only
 * in effects.css because they are scoped to a @media block and cannot be
 * targeted by setProperty on :root.
 */

export const EFFECTS = {
  bounce: {
    title:      'Bounce',
    color:      '#2F1847',   /* deep purple  */
    colorDark:  '#1d0f2c',
    accent:     '#c026d3',   /* vivid fuchsia        */
    complement: '#16a34a',   /* vivid green  — 180°  */
    params:     { min: 0, max: 127 },
  },
  flipflop: {
    title:      'Flip Flop',
    color:      '#0B61DB',   /* blue         */
    colorDark:  '#0848a4',
    accent:     '#2563eb',   /* vivid blue           */
    complement: '#ea580c',   /* vivid orange — 180°  */
    params:     { flip: 0, flop: 127 },
  },
  glitch: {
    title:      'Glitch',
    color:      'oklch(59.939% 0.191 335.171)', /* magenta */
    colorDark:  'oklch(45% 0.2 335.171)',
    accent:     '#e11d48',   /* vivid rose           */
    complement: '#059669',   /* vivid teal   — 180°  */
    params:     { min: 0, max: 127, focus: 100, spread: 0, bias: 0, beat_affinity: 0 },
  },
  swell: {
    title:      'Swell',
    color:      '#06AED5',   /* cyan         */
    colorDark:  '#0487a7',
    accent:     '#0891b2',   /* vivid cyan           */
    complement: '#c2410c',   /* burnt orange — 180°  */
    params:     { min: 0, max: 127, balance: 0, gradient: 0, skew: 0 },
  },
  zigzag: {
    title:      'Zig Zag',
    color:      '#F9DC5C',   /* yellow       */
    colorDark:  '#d4b832',
    accent:     '#ca8a04',   /* vivid amber          */
    complement: '#7c3aed',   /* vivid violet — 180°  */
    params:     { min: 0, max: 127, skew: 0 },
  },
  wander: {
    title:      'Wander',
    color:      '#7B4E2D',   /* warm brown   */
    colorDark:  '#562f15',
    accent:     '#029869',   /* green                */
    complement: '#0c7ec2',   /* steel blue   — 180°  */
    params:     { min: 0, max: 127, step_size: 10, boundary_mode: 'soft' },
  },
};

/**
 * Injects all effect colour values as CSS custom properties on :root.
 * Call once at app startup (e.g. from App.vue mounted()).
 *
 * Sets vars of the form:
 *   --bounce-color, --bounce-color-dark, --bounce-accent, --bounce-complement
 *   --flipflop-color, …  etc.
 */
export function applyEffectCssVars() {
  const root = document.documentElement;
  for (const [type, { color, colorDark, accent, complement }] of Object.entries(EFFECTS)) {
    root.style.setProperty(`--${type}-color`,      color);
    root.style.setProperty(`--${type}-color-dark`, colorDark);
    root.style.setProperty(`--${type}-accent`,     accent);
    root.style.setProperty(`--${type}-complement`, complement);
  }
}

/**
 * Convenience map of effect type → accent colour hex string.
 * Derived from EFFECTS so it never goes stale.
 */
export const EFFECT_ACCENT = Object.fromEntries(
  Object.entries(EFFECTS).map(([type, { accent }]) => [type, accent])
);

/**
 * Convenience map of effect type → display title.
 * Derived from EFFECTS so it never goes stale.
 */
export const EFFECT_TITLES = Object.fromEntries(
  Object.entries(EFFECTS).map(([type, { title }]) => [type, title])
);

/**
 * Effect types that support an Accent knob.
 */
export const ACCENT_EFFECT_TYPES = ['bounce', 'swell', 'zigzag', 'flipflop'];

/**
 * Default parameter values for a newly created effect of each type.
 *
 * These mirror the firmware's own defaults exactly — every effect class reads
 * its config with config.get("<param>", <default>), and these are those
 * defaults. They have to match: the editor and the device both fill in an
 * absent key, and if they disagree the knob shows one value while the device
 * plays another (MDL-371 — an effect created without a `max` read 0 in the
 * editor and ran at 127 on the device).
 *
 * Note flipflop has flip/flop rather than min/max, and no min/max at all.
 */

/** Type of the effect the editors create when you press Add. */
export const DEFAULT_EFFECT_TYPE = 'swell';

/** Params shared by every type, whatever it is. */
const COMMON_PARAMS = { clock_sync: true, rate: '1' };

/**
 * Build a complete effect of the given type.
 *
 * Every param the type understands is present, so nothing is left for the
 * editor and the device to default differently. `overrides` is applied last —
 * midulate-editor uses it to seed channel/cc, which the web editor
 * deliberately leaves unset so no MIDI is emitted until the user has chosen a
 * destination.
 */
export function createEffect(type = DEFAULT_EFFECT_TYPE, overrides = {}) {
  const spec = EFFECTS[type];
  if (!spec) throw new Error(`createEffect: unknown effect type "${type}"`);

  const effect = { type, ...COMMON_PARAMS, ...spec.params };
  // NB: effect.accent is the accent *setting*; EFFECTS[type].accent above is a
  // colour. Unrelated keys that happen to share a name.
  if (ACCENT_EFFECT_TYPES.includes(type)) effect.accent = 'none';

  return { ...effect, ...overrides };
}

/**
 * Params that belong to the effect rather than to its type, and so survive a
 * type change. Everything else is replaced by the new type's defaults.
 */
const CARRIED_OVER = ['channel', 'cc', 'clock_sync', 'rate', 'mappings'];

/**
 * Change an effect's type in place, swapping its params for the new type's.
 *
 * In place because the editors mutate the effect object they were handed
 * rather than emitting a replacement. Without this a retyped effect keeps the
 * old type's params as dead keys and has none of the new type's — the same
 * editor/device disagreement as an uninitialised effect, reached by a
 * different route.
 */
export function retypeEffect(effect, type) {
  const carried = {};
  for (const key of CARRIED_OVER) {
    if (effect[key] !== undefined) carried[key] = effect[key];
  }

  for (const key of Object.keys(effect)) delete effect[key];
  Object.assign(effect, createEffect(type, carried));

  return effect;
}
